import {copyObj} from "@/scripts/general";
import {API} from "@/api/generated";
import {
  sortDataName,
  sortDataStatus
} from "@/page/onlineAppointment/doctors/DoctorsScript";
import type {
  doctor,
  doctorsState
} from "@/page/onlineAppointment/doctors/DoctorsType";
import {LegacyDoctorPhoto} from "@/page/quiz/typeQuiz";
import {defineStore} from "pinia";

const normalizeDoctorPhoto = (photo: LegacyDoctorPhoto): string => {
  if (typeof photo === "string") {
    return photo;
  }

  if (photo && typeof photo === "object" && !Array.isArray(photo)) {
    if (typeof photo.circle === "string" && photo.circle) {
      return photo.circle;
    }

    if (typeof photo.square === "string" && photo.square) {
      return photo.square;
    }

    const firstStringPhoto = Object.values(photo).find((value) => {
      return typeof value === "string" && value;
    });

    return typeof firstStringPhoto === "string" ? firstStringPhoto : "";
  }

  return "";
};

const normalizeDoctor = (doctorItem: doctor): doctor => {
  const normalizedDoctor = copyObj(doctorItem);

  normalizedDoctor.photo = normalizeDoctorPhoto(doctorItem.photo);

  if (!normalizedDoctor.service) {
    normalizedDoctor.service = {};
  }

  if (!normalizedDoctor.address) {
    normalizedDoctor.address = {};
  }

  if (!normalizedDoctor.specialization) {
    normalizedDoctor.specialization = [];
  }

  if (normalizedDoctor.discription == null) {
    normalizedDoctor.discription = "";
  }

  if (normalizedDoctor.experience == null) {
    normalizedDoctor.experience = "";
  }

  if (normalizedDoctor.name == null) {
    normalizedDoctor.name = "";
  }

  return normalizedDoctor;
};

const normalizeDoctorList = (
  dataList: doctor[] | doctor[][] | string | null | undefined
): doctor[] => {
  if (typeof dataList === "string" || dataList == null) {
    return [];
  }

  const flatDataList = dataList.flat();

  return flatDataList.map((doctorItem) => normalizeDoctor(doctorItem));
};

const createEmptyDoctor = (): doctor => ({
  _id: "",
  id: 0,
  userId: "",
  name: "",
  service: {},
  address: {},
  experience: "",
  specialization: [],
  status: false,
  photo: "",
  create_at: "",
  updated_at: ""
});

export const useDoctorsStore = defineStore("doctorData", {
  state: (): doctorsState => ({
    statusDataLouding: "",
    data: [[], []],
    buferStatus: {},
    settingDoctor: createEmptyDoctor(),
    servicesList: {},
    modelDirection: []
  }),

  actions: {
    async fetchData(): Promise<void> {
      this.setStatusDataLouding("loading");
      const doctors = await API.getDoctors();
      this.setData(doctors);
    },

    async updateData(): Promise<void> {
      const doctorList = this.data.flat().map((doctorItem) => {
        return normalizeDoctor(doctorItem);
      });

      const doctors = await API.updateDoctors({body: {doctorList}});
      this.setData(doctors);
    },

    setServicesList(newData: doctorsState["servicesList"]): void {
      this.servicesList = {...newData};
    },

    updateDoctor(item: doctor): void {
      const normalizedItem = normalizeDoctor(item);

      this.data.forEach((group, groupIndex) => {
        const index = group.findIndex((doctorItem) => {
          return (
            doctorItem._id &&
            normalizedItem._id &&
            doctorItem._id === normalizedItem._id
          );
        });

        if (index !== -1) {
          this.data[groupIndex][index] = copyObj(normalizedItem);
        }
      });

      if (
        this.settingDoctor?._id &&
        normalizedItem._id &&
        this.settingDoctor._id === normalizedItem._id
      ) {
        this.settingDoctor = copyObj(normalizedItem);
      }
    },

    setSettingDoctor(newData: doctor): void {
      this.settingDoctor = copyObj(normalizeDoctor(newData));
    },

    setModelDirection(newData: string[]): void {
      this.modelDirection = [...newData];
    },

    setData(dataList: doctor[] | doctor[][] | string | null | undefined): void {
      if (typeof dataList === "string" || dataList == null) {
        this.data = [[], []];
        this.settingDoctor = createEmptyDoctor();
        this.statusDataLouding = "not key";
        return;
      }

      const normalizedDataList = normalizeDoctorList(dataList);
      const dataTrue: doctor[] = [];
      const dataFalse: doctor[] = [];

      normalizedDataList.forEach((dataItem) => {
        if (dataItem.status === true) {
          dataTrue.push(dataItem);
        } else {
          dataFalse.push(dataItem);
        }
      });

      dataTrue.sort(sortDataName).sort(sortDataStatus);
      dataFalse.sort(sortDataName).sort(sortDataStatus);

      this.data = [dataTrue, dataFalse];

      if (this.settingDoctor?._id) {
        const actualDoctor =
          dataTrue.find(
            (doctorItem) => doctorItem._id === this.settingDoctor._id
          ) ||
          dataFalse.find(
            (doctorItem) => doctorItem._id === this.settingDoctor._id
          );

        if (actualDoctor) {
          this.settingDoctor = copyObj(actualDoctor);
        } else {
          this.settingDoctor = createEmptyDoctor();
        }
      }

      this.statusDataLouding = "success";
    },

    setBuferStatus(newData: doctorsState["buferStatus"]): void {
      this.buferStatus = {...newData};
    },

    setStatusDataLouding(status: string): void {
      this.statusDataLouding = status;
    }
  },

  getters: {
    getStatusDataLouding: (
      state: doctorsState
    ): doctorsState["statusDataLouding"] => {
      return state.statusDataLouding;
    },

    getData: (state: doctorsState): doctor[][] => {
      return state.data;
    },

    getBuferStatus: (state: doctorsState): doctorsState["buferStatus"] => {
      return state.buferStatus;
    },

    getSettingDoctor: (state: doctorsState): doctor => {
      return state.settingDoctor;
    },

    getServicesList: (state: doctorsState): doctorsState["servicesList"] => {
      return state.servicesList;
    },

    getModelDirection: (state: doctorsState): string[] => {
      return state.modelDirection;
    }
  }
});
